/* eslint-disable @typescript-eslint/ban-types */
import React from 'react'
import ClientSuspense from '@components/ClientSuspense'
import DeliveryType from '@components/fields/DeliveryType'
import LoadingText from '@components/LoadingText'
import SuspenseLoading from '@components/SuspenseLoading'
import withWebsiteId from '@helpers/websites/withWebsiteId'
import Select from '@packages/justo-parts/lib/components/fields/Select'
import withMessage from '@packages/justo-parts/lib/decorators/withMessage'
import withGraphQL from '@packages/react-apollo-decorators/lib/withGraphQL'
import withMutation from '@packages/react-apollo-decorators/lib/withMutation'
import StoreName from '@page-components/Checkout/Checkout/Delivery/StoreName'
import autobind from 'autobind-decorator'
import gql from 'graphql-tag'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import {NextRouter, withRouter} from 'next/router'
import {TFunction, withTranslation} from 'next-i18next'
import {Field, Form} from 'simple-react-form'

import DeliveryOptions from './DeliveryOptions'
import StoreMessage from './StoreMessage'
import TableName from './TableName'
import Time from './Time'

import styles from './styles.module.css'
export interface UpdateBasicOptionsProps {
  router: NextRouter
  showMessage?: Function
  setPreferencesBasicOptions?: Function
  stores?: any
  preferences: any
  t: TFunction
  isFromHeader?: boolean
  showStoreName?: boolean
  layout?: string
}

@withMutation(gql`
  mutation setPreferencesBasicOptions(
    $websiteId: ID
    $storeId: ID
    $placeId: ID
    $deliveryType: ID
  ) {
    setUserPreferences(
      websiteId: $websiteId
      storeId: $storeId
      placeId: $placeId
      deliveryType: $deliveryType
    ) {
      _id
      deliveryType
      timeLabel
      time
      itsStoreOpenAtTime
      placeId
      menuId
      tableName
      directions {
        duration
      }
      website {
        _id
        name
      }
      websiteId
      storeId
      deliverAt
      time
      channel
      shouldHidePrice
      store {
        _id
        name
        schedule {
          itsOpenNow: itsAvailableAt(
            websiteId: $websiteId
            storeId: $storeId
            shouldAddMessage: true
          )
        }
        schedulingOrdersDays
        minimumAnticipationDays
        currentPreparationDuration
        currentDeliveryDuration
        timeFormat
        availableScheduleDaysOptions {
          label
          value
        }
        nextAvailableDate
      }
    }
  }
`)
@withWebsiteId
@withGraphQL(
  gql`
    query getDataForBasicInfo($websiteId: ID) {
      stores(websiteId: $websiteId) {
        items {
          label: name
          value: _id
          acceptServe
          acceptGo
          schedulingOrdersDays
          address {
            placeId
          }
        }
      }
    }
  `,
  {loading: null}
)
@withMessage
export class UpdateBasicOptions extends React.Component<UpdateBasicOptionsProps> {
  state = {
    loading: false
  }

  @autobind
  async onChange(params) {
    try {
      if (!this.props.preferences.websiteId) {
        return
      }

      const fields = ['websiteId', 'storeId', 'placeId', 'deliveryType']
      const final = pick(
        {
          ...this.props.preferences,
          ...params
        },
        ...fields
      )

      if (isEqual(pick(this.props.preferences, ...fields), final)) return
      this.setState({loading: true})

      await this.props.setPreferencesBasicOptions(final, {
        // refetchQueries: ['getMyOrderPreferences', 'getUserPreferences']
      })
    } catch (error) {
      this.props.showMessage(error)
    }
    this.setState({loading: false})
  }

  renderStore() {
    const {stores = {items: []}, preferences = {}} = this.props
    const {deliveryType} = preferences

    if (!deliveryType) {
      return <StoreMessage preferences={preferences} />
    }

    const filteredStores = stores.items.filter(store => {
      if (!store.address || !store.address.placeId) {
        return false
      }
      if (deliveryType === 'go' && store.acceptGo) {
        return true
      }
      if (deliveryType === 'serve' && store.acceptServe) {
        return true
      }
      return false
    })

    if (filteredStores.length === 1) {
      if (!this.props.preferences) return
      if (this.state.loading) return
      if (this.props.preferences.storeId !== filteredStores[0].value) {
        setTimeout(() => this.onChange({storeId: filteredStores[0].value}), 0)
      }
      return
    }

    return (
      <Field
        fieldName="storeId"
        label={this.props.t('order.options.updateBasicOptions.labelChooseStore')}
        placeholder={this.props.t('order.options.updateBasicOptions.labelSelectStore')}
        type={Select}
        className={styles.addressSelector}
        options={filteredStores}
      />
    )
  }

  renderTime() {
    const {placeId, deliveryType, store, time} = this.props.preferences
    if (!store) return
    if (deliveryType === 'serve' && !store.acceptServeSchedulingOrders) return
    if (deliveryType === 'serve' && time === 'now') return

    return (
      <Time
        preferences={this.props.preferences}
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        showDirections={!!placeId && placeId !== 'no-deliver'}
      />
    )
  }

  renderTableName() {
    const {deliveryType, store, time} = this.props.preferences
    if (!store) return
    if (deliveryType !== 'serve') return
    if (time !== 'now') return

    return <TableName preferences={this.props.preferences} />
  }

  renderDeliveryOptions() {
    const {loading} = this.state
    if (loading) return <LoadingText lines={2} />
    const {deliveryType} = this.props.preferences
    return (
      <>
        {deliveryType === 'delivery' && (
          <DeliveryOptions
            preferences={this.props.preferences}
            isFromHeader={this.props.isFromHeader}
            layout={this.props.layout}
          />
        )}
        {deliveryType !== 'delivery' && this.renderStore()}
        {deliveryType !== 'delivery' && this.renderTime()}
        {deliveryType !== 'delivery' && this.renderTableName()}
      </>
    )
  }

  render() {
    return (
      <ClientSuspense fallback={<SuspenseLoading />}>
        <div className={styles.container}>
          <Form state={this.props.preferences} onChange={this.onChange}>
            <Field fieldName="deliveryType" type={DeliveryType} disabled={this.state.loading} />
            {this.renderDeliveryOptions()}
            <StoreName
              preferences={this.props.preferences}
              website={{showStoreName: this.props.showStoreName}}
              isOnMenu
            />
          </Form>
        </div>
      </ClientSuspense>
    )
  }
}

export default withTranslation()(withRouter<UpdateBasicOptionsProps>(UpdateBasicOptions))
